import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import axios from 'axios';
import apiService from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
// Import strategy-specific charts
import FractalChart from './charts/FractalChart';
import StableChart from './StableChart'; // Keep as fallback

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const BacktestResults = ({ result, onBackToForm }) => {
  const [activeTab, setActiveTab] = useState('metrics');
  const [isExporting, setIsExporting] = useState(false);
  const [priceData, setPriceData] = useState([]);
  const [premiumData, setPremiumData] = useState([]);
  const [chartType, setChartType] = useState('index'); // 'index' or 'premium'

  // Fetch real index data for the chart instead of using synthetic data
  useEffect(() => {
    if (!result) return;
    
    console.log('=== BACKTEST RESULT RECEIVED ===');
    console.log('Timestamp:', new Date().toISOString());
    
    // DIRECT INSPECTION OF PREMIUM DATA
    console.log('DIRECT PREMIUM DATA ACCESS:');
    console.log('premium_data directly:', result.premium_data);
    
    // Initialize premium data if it exists
    if (result.premium_data && Array.isArray(result.premium_data) && result.premium_data.length > 0) {
      console.log('Setting premium data from array of length:', result.premium_data.length);
      setPremiumData(result.premium_data);
    } else if (result.premium_data && typeof result.premium_data === 'object' && !Array.isArray(result.premium_data)) {
      // Handle case where premium_data exists but isn't an array
      console.log('Premium data exists but is not properly formatted:', result.premium_data);
      try {
        // Convert object to array if possible
        const premiumArray = Object.values(result.premium_data);
        console.log('Converted premium data object to array with length:', premiumArray.length);
        setPremiumData(premiumArray);
      } catch (error) {
        console.error('Failed to convert premium data to array:', error);
      }
    }
    
    // Determine chart type based on strategy name
    if (result.strategy_name) {
      const strategyName = result.strategy_name.toLowerCase();
      
      if (strategyName.includes('combined_premium') || strategyName.includes('supertrend_combined_premium')) {
        console.log('Setting chart type to PREMIUM for strategy:', result.strategy_name);
        setChartType('premium');
      } else if (strategyName.includes('fractal') || strategyName.includes('daily_trading_range')) {
        console.log('Setting chart type to INDEX for strategy:', result.strategy_name);
        setChartType('index');
      } else {
        console.log('Using default INDEX chart type for strategy:', result.strategy_name);
        setChartType('index');
      }
    }
    
    // Strategy name check for logging
    if (result.strategy_name && result.strategy_name.toLowerCase().includes('combined_premium')) {
      console.log('Strategy is Supertrend Combined Premium');
    } else if (result.strategy_name) {
      console.log('Strategy is:', result.strategy_name);
    }
    
    // Check if we already have price data in the result
    console.log('=== PRICE DATA DEBUG ===');
    console.log('Full result keys:', Object.keys(result));
    console.log('result.price_data exists:', !!result.price_data);
    console.log('result.price_data length:', result.price_data ? result.price_data.length : 'N/A');
    console.log('result.strategy_name:', result.strategy_name);
    
    // DEBUG: Detailed trades analysis
    console.log('\n🔍 FRONTEND TRADES DEBUG:');
    console.log('result.trades exists:', !!result.trades);
    console.log('result.trades length:', result.trades ? result.trades.length : 'N/A');
    console.log('Sample trade for reference:', result.trades?.[0]);
    
    if (result.trades && result.trades.length > 0) {
      // Show trade dates distribution
      const tradeDates = result.trades.map(trade => trade.timestamp ? trade.timestamp.substring(0, 10) : 'Unknown');
      const uniqueDates = [...new Set(tradeDates)].sort();
      console.log('Trade dates received:', uniqueDates.slice(0, 5), '...', uniqueDates.slice(-5));
      console.log('Frontend date distribution:', {
        Jan: tradeDates.filter(d => d.startsWith('2025-01')).length,
        Feb: tradeDates.filter(d => d.startsWith('2025-02')).length, 
        Mar: tradeDates.filter(d => d.startsWith('2025-03')).length,
        Apr: tradeDates.filter(d => d.startsWith('2025-04')).length
      });
      
      // Show first and last trades
      console.log('First trade:', result.trades[0]);
      console.log('Last trade:', result.trades[result.trades.length - 1]);
    }
    
    // Check for price data in different possible field names - ENHANCED FOR DATABASE LOADING
    let priceDataToUse = null;
    
    // Priority order for chart data loading:
    if (result.price_data && result.price_data.length > 0) {
      priceDataToUse = result.price_data;
      console.log('🎯 USING PRICE_DATA from new backtest');
    } else if (result.premium_data && result.premium_data.length > 0) {
      priceDataToUse = result.premium_data;
      console.log('🎯 USING PREMIUM_DATA from saved result (price_data was saved as premium_data)');
    } else if (result.data && result.data.price_data && result.data.price_data.length > 0) {
      priceDataToUse = result.data.price_data;
      console.log('🎯 USING NESTED PRICE_DATA');
    } else {
      // Additional checks for database loading variations
      console.log('🔍 DEBUGGING: Checking all result fields for chart data...');
      console.log('Available result keys:', Object.keys(result));
      
      // Check if premium_data is a string (JSON) that needs parsing
      if (typeof result.premium_data === 'string') {
        try {
          const parsedPremiumData = JSON.parse(result.premium_data);
          if (parsedPremiumData && parsedPremiumData.length > 0) {
            priceDataToUse = parsedPremiumData;
            console.log('🎯 USING PARSED PREMIUM_DATA from saved result');
          }
        } catch (e) {
          console.log('❌ Failed to parse premium_data string:', e);
        }
      }
    }
    
    if (priceDataToUse && priceDataToUse.length > 0) {
      console.log('📊 FOUND VALID CHART DATA!');
      console.log('Chart data points:', priceDataToUse.length);
      console.log('Chart data sample:', priceDataToUse.slice(0, 2));
      console.log('Price range:', priceDataToUse[0]?.close, 'to', priceDataToUse[priceDataToUse.length-1]?.close);
      console.log('Timestamp range:', priceDataToUse[0]?.timestamp, 'to', priceDataToUse[priceDataToUse.length-1]?.timestamp);
      setPriceData(priceDataToUse);
      return;
    }
    // No price_data in result; fetch real index OHLC as fallback (NOT synthetic)
    (async () => {
      try {
        console.log('ℹ️ No price_data in result. Fetching index OHLC from backend...');
        // Resolve symbol id compatible with backend
        const resolveSymbol = (sym) => {
          if (!sym) return 'nifty';
          if (typeof sym === 'object' && sym.id) return String(sym.id).toLowerCase();
          const s = String(sym).toLowerCase();
          if (s.includes('bank')) return 'banknifty';
          if (s.includes('fin')) return 'finnifty';
          return 'nifty';
        };
        const symbolId = resolveSymbol(result.symbol);
        // Use backtested timeframe if available (minutes)
        const tfRaw = (result.parameters && result.parameters.timeframe) 
          ? result.parameters.timeframe 
          : undefined;
        const tf = Number(tfRaw);
        // To avoid backend 500 on resampling, fetch base data without timeframe
        const resp = await apiService.marketData.getIndexData(
          symbolId,
          result.start_date,
          result.end_date,
          undefined
        );
        let ohlc = resp?.data?.data || resp?.data || [];
        if (Array.isArray(ohlc) && ohlc.length > 0) {
          console.log('✅ Loaded fallback OHLC (base):', ohlc.length, 'points');
          // If a valid timeframe is provided, resample on the client
          if (Number.isFinite(tf) && tf > 0) {
            const resample = (rows, minutes) => {
              const bucketMap = new Map();
              for (const r of rows) {
                if (!r.timestamp) continue;
                const d = new Date(r.timestamp);
                const ms = d.getTime();
                const bucketMs = Math.floor(ms / (minutes * 60 * 1000)) * (minutes * 60 * 1000);
                const key = bucketMs;
                const open = Number(r.open) || 0;
                const high = Number(r.high) || open;
                const low = Number(r.low) || open;
                const close = Number(r.close) || open;
                const volume = Number(r.volume || 0);
                if (!bucketMap.has(key)) {
                  bucketMap.set(key, { open, high, low, close, volume, timestamp: new Date(bucketMs).toISOString() });
                } else {
                  const b = bucketMap.get(key);
                  // first open stays
                  b.high = Math.max(b.high, high);
                  b.low = Math.min(b.low, low);
                  b.close = close; // last close wins
                  b.volume = (b.volume || 0) + volume;
                }
              }
              // Sort by time asc
              return Array.from(bucketMap.entries())
                .sort((a, b) => a[0] - b[0])
                .map(([, v]) => v);
            };
            const resampled = resample(ohlc, tf);
            console.log(`⏱️ Client-resampled to ${tf}m:`, resampled.length, 'points');
            ohlc = resampled;
          }
          setPriceData(ohlc);
        } else {
          console.warn('⚠️ Fallback OHLC response empty or invalid');
        }
      } catch (e) {
        console.error('❌ Failed to fetch fallback index OHLC:', e?.message || e);
        if (e?.response) {
          console.error('Status:', e.response.status, 'Data:', e.response.data);
        }
      }
    })();
  }, [result, chartType]);

  // Format metrics for display
  const formatMetric = (value, type) => {
    // Handle undefined or null values
    if (value === undefined || value === null) {
      return 'N/A';
    }
    
    if (type === 'percentage') {
      return (value * 100).toFixed(2) + '%';
    } else if (type === 'currency') {
      return value.toFixed(2);
    } else {
      return value;
    }
  };

  // Prepare equity curve data for chart
  const prepareEquityCurveData = () => {
    if (!result || !result.equity_curve || result.equity_curve.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: 'Equity Curve',
            data: [],
            borderColor: 'rgb(99, 102, 241)',
            backgroundColor: 'rgba(99, 102, 241, 0.5)',
            tension: 0.1,
          },
        ],
      };
    }

    // Format timestamps and ensure data is sorted by timestamp
    const sortedData = [...result.equity_curve].sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );

    return {
      labels: sortedData.map(point => {
        const date = new Date(point.timestamp);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }),
      datasets: [
        {
          label: 'Equity Curve',
          data: sortedData.map(point => point.equity),
          borderColor: 'rgb(99, 102, 241)',
          backgroundColor: 'rgba(99, 102, 241, 0.5)',
          tension: 0.1,
        },
      ],
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Equity Curve',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Equity',
        },
      },
    },
  };

  // Function to download backtest report as PDF
  const downloadPDF = async () => {
    try {
      setIsExporting(true);
      // Get the API URL from the environment or use default
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('You must be logged in to export results');
        setIsExporting(false);
        return;
      }
      
      // Make the request to the backend
      const response = await axios({
        method: 'GET',
        url: `${apiUrl}/backtest/results/${result.id}/pdf`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });
      
      // Create a URL for the blob data
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create a temporary link element and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backtest_${result.strategy_name}_${result.symbol}_${result.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      setIsExporting(false);
    } catch (error) {
      console.error('Error downloading PDF', error);
      alert('Failed to download PDF report');
      setIsExporting(false);
    }
  };

  return (
    <div className="px-4 py-5 sm:p-6">
      {/* Header with back button */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Backtest Results
        </h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={downloadPDF}
            disabled={isExporting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            {isExporting ? 'Exporting...' : 'Export as PDF'}
          </button>
          <button
            type="button"
            onClick={onBackToForm}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Run Another Backtest
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            className={`${
              activeTab === 'metrics'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            onClick={() => setActiveTab('metrics')}
          >
            Metrics
          </button>
          <button
            className={`${
              activeTab === 'pricechart'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            onClick={() => setActiveTab('pricechart')}
          >
            Price Chart
          </button>
          <button
            className={`${
              activeTab === 'trades'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            onClick={() => setActiveTab('trades')}
          >
            Trades
          </button>
          <button
            className={`${
              activeTab === 'equity'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            onClick={() => setActiveTab('equity')}
          >
            Equity Curve
          </button>
          {(result && result.performance_breakdown) && (
            <button
              className={`${
                activeTab === 'breakdown'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              onClick={() => setActiveTab('breakdown')}
            >
              Breakdown
            </button>
          )}
          <button
            className={`${
              activeTab === 'parameters'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            onClick={() => setActiveTab('parameters')}
          >
            Parameters
          </button>
        </nav>
      </div>

      {/* Tab content */}
      <div className="mt-6">
        {/* Metrics Tab */}
        {activeTab === 'metrics' && (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Performance Metrics
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Total Profit/Loss</dt>
                  <dd className={`mt-1 text-sm sm:mt-0 sm:col-span-2 ${result.metrics.total_profit >= 0 ? 'text-green-600' : 'text-red-600'} font-semibold`}>
                    {result.metrics.total_profit >= 0 ? '+' : ''}
                    {formatMetric(result.metrics.total_profit, 'currency')}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Total Trades</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {result.metrics.total_trades}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Winning Trades</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {result.metrics.winning_trades} ({formatMetric(result.metrics.win_rate, 'percentage')})
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Losing Trades</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {result.metrics.losing_trades} ({formatMetric(1 - result.metrics.win_rate, 'percentage')})
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Profit Factor</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatMetric(result.metrics.profit_factor, 'number')}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Max Drawdown</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatMetric(result.metrics.max_drawdown, 'currency')}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Sharpe Ratio</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatMetric(result.metrics.sharpe_ratio, 'number')}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Execution Time</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {result.metrics.execution_time_formatted || `${formatMetric(result.metrics.execution_time_seconds || 0, 'number')} seconds`}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        {/* Breakdown Tab (Supertrend 15-05 only; guarded by presence of performance_breakdown) */}
        {activeTab === 'breakdown' && result && result.performance_breakdown && (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Performance Breakdown
              </h3>
              <p className="mt-1 text-sm text-gray-500">PnL grouped by expiry and time periods for this strategy.</p>
            </div>
            <div className="px-6 pb-6">
              {/* Helper renderers */}
              {(() => {
                const bd = result.performance_breakdown || {};
                const renderTable = (title, rows) => (
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-gray-800 mb-2">{title}</h4>
                    {rows && rows.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bucket</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PnL (₹)</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trades</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wins</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Losses</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Win %</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg PnL</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {rows.map((r, idx) => (
                              <tr key={`${title}-${idx}`}>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{r.key}</td>
                                <td className={`px-4 py-2 whitespace-nowrap text-sm font-medium ${r.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>{r.pnl >= 0 ? '+' : ''}₹{r.pnl.toFixed(2)}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{r.trades}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{r.wins}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{r.losses}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{(r.win_rate * 100).toFixed(2)}%</td>
                                <td className={`px-4 py-2 whitespace-nowrap text-sm ${r.avg_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>{r.avg_pnl >= 0 ? '+' : ''}₹{r.avg_pnl.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No data</div>
                    )}
                  </div>
                );

                return (
                  <>
                    {renderTable('By Expiry', bd.by_expiry || [])}
                    {renderTable('By Year', bd.by_year || [])}
                    {renderTable('By Quarter', bd.by_quarter || [])}
                    {renderTable('By Month', bd.by_month || [])}

                    {/* Closed positions detail */}
                    <h4 className="text-md font-semibold text-gray-800 mb-2 mt-6">Closed Positions</h4>
                    {result.closed_positions && result.closed_positions.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exit</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exit Reason</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PnL (₹)</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {result.closed_positions.map((p, idx) => (
                              <tr key={`cp-${idx}`}>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{p.entry_time}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{p.exit_time}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{p.expiry}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{p.type}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{(p.exit_reason || '').replace('_',' ')}</td>
                                <td className={`px-4 py-2 whitespace-nowrap text-sm font-medium ${p.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>{p.pnl >= 0 ? '+' : ''}₹{Number(p.pnl).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No closed positions yet.</div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Trades Tab */}
        {activeTab === 'trades' && (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Trade List
                </h3>
                {result.trades && result.trades.length > 0 && (
                  <div className="flex space-x-4 text-sm text-gray-500">
                    <span>Total: <strong>{result.trades.length}</strong> trades</span>
                    <span>|</span>
                    <span>Buy: <strong className="text-green-600">
                      {result.trades.filter(t => t.action.toLowerCase() === 'buy').length}
                    </strong></span>
                    <span>|</span>
                    <span>Sell: <strong className="text-red-600">
                      {result.trades.filter(t => t.action.toLowerCase() === 'sell').length}
                    </strong></span>
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              {result.trades && result.trades.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trade Description
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Strike & Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price & Qty
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Value (₹)
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trade Type
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {result.trades.map((trade, index) => {
                      // Create descriptive trade description
                      const formatTradeDescription = (trade) => {
                        const action = trade.action.toUpperCase();
                        const price = trade.price.toFixed(2);
                        const qty = trade.quantity;
                        
                        if (trade.symbol_info?.index) {
                          const strike = trade.symbol_info.strike;
                          const optionType = trade.symbol_info.option_type?.toUpperCase();
                          return `${action} ${qty} × ${trade.symbol_info.index} ${strike} ${optionType} @ ₹${price}`;
                        } else {
                          return `${action} ${qty} × Option @ ₹${price}`;
                        }
                      };

                      const formatDateTime = (timestamp) => {
                        const date = new Date(timestamp);
                        const dateStr = date.toLocaleDateString('en-IN');
                        const timeStr = date.toLocaleTimeString('en-IN', { hour12: false });
                        return { dateStr, timeStr };
                      };

                      const { dateStr, timeStr } = formatDateTime(trade.timestamp);
                      const tradeDescription = formatTradeDescription(trade);
                      const totalValue = Math.abs(trade.value);

                      const exitTrue = Array.isArray(trade.exit_true) ? trade.exit_true.filter(Boolean) : [];
                      const exitFlags = trade.exit_flags && typeof trade.exit_flags === 'object' ? trade.exit_flags : null;
                      const isExitTrade = trade.action?.toLowerCase() === 'sell' && trade.trade_type && trade.trade_type !== 'entry';

                      const exitFlagsText = exitFlags
                        ? `sl_exit=${exitFlags.sl_exit ? 'true' : 'false'}, mode_reversal=${exitFlags.mode_reversal ? 'true' : 'false'}, trend_check_exit=${exitFlags.trend_check_exit ? 'true' : 'false'}, time_exit=${exitFlags.time_exit ? 'true' : 'false'}`
                        : null;

                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="font-medium">{dateStr}</div>
                            <div className="text-gray-500">{timeStr}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className={`font-medium ${trade.action.toLowerCase() === 'buy' ? 'text-green-600' : 'text-red-600'}`}>
                              {tradeDescription}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Symbol: {trade.symbol}
                            </div>
                            {isExitTrade && (
                              <div className="text-xs text-gray-500 mt-1">
                                <div>Exit true: {exitTrue.length > 0 ? exitTrue.join(' | ') : trade.trade_type}</div>
                                {exitFlagsText && <div>Exit flags: {exitFlagsText}</div>}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {trade.symbol_info?.strike ? (
                              <div>
                                <div className="font-medium">{trade.symbol_info.strike}</div>
                                <div className="text-xs text-gray-500">{trade.symbol_info.option_type?.toUpperCase()}</div>
                              </div>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="font-medium">₹{trade.price.toFixed(2)}</div>
                            <div className="text-xs text-gray-500">Qty: {trade.quantity}</div>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${trade.value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {trade.value >= 0 ? '+' : '-'}₹{totalValue.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              trade.trade_type.includes('entry') ? 'bg-blue-100 text-blue-800' : 
                              trade.trade_type.includes('exit') ? 'bg-purple-100 text-purple-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {trade.trade_type.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="px-6 py-4 text-center text-gray-500">
                  No trades found.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Price Chart Tab */}
        {activeTab === 'pricechart' && (
          <div className="bg-white shadow overflow-hidden rounded-lg" style={{ width: '100%' }}>
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Price Chart with Buy/Sell Signals
              </h3>
              <div className="mt-1 flex items-center justify-between">
                <p className="max-w-2xl text-sm text-gray-500">
                  {chartType === 'index' 
                    ? `${result.symbol} index price with buy/sell signals` 
                    : `Combined Premium (CALL+PUT) with Supertrend overlay`}
                </p>
                {/* Only show toggle if premium data is available */}
                {result.premium_data && result.premium_data.length > 0 && (
                  <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
                    <button
                      onClick={() => {
                        console.log('Setting chart type to index');
                        setChartType('index');
                      }}
                      className={`px-3 py-1 text-sm rounded-md ${chartType === 'index' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-700'}`}
                    >
                      Index Price
                    </button>
                    <button
                      onClick={() => {
                        console.log('Setting chart type to premium');
                        setChartType('premium');
                      }}
                      className={`px-3 py-1 text-sm rounded-md ${chartType === 'premium' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-700'}`}
                    >
                      Combined Premium
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6" style={{ width: '100%', maxWidth: '100%' }}>
              {/* Show message if no data for selected chart type */}
              {(chartType === 'index' && (!priceData || priceData.length === 0)) ||
               (chartType === 'premium' && (!premiumData || premiumData.length === 0)) ? (
                <div className="text-center text-gray-500">
                  {chartType === 'premium' && (!premiumData || premiumData.length === 0) ? 
                    'No combined premium data available for this strategy.' : 
                    'Loading price data...'}
                </div>
              ) : (
                <div className="price-chart-container" style={{ height: '500px', width: '100%', maxWidth: '100vw', position: 'relative', overflow: 'visible', paddingBottom: '8px' }}>
                  {/* Debug info */}
                  <div className="mb-2 text-xs text-gray-500">
                    Chart Type: {chartType} | 
                    Premium Data: {premiumData?.length || 0} items | 
                    Price Data: {priceData?.length || 0} items
                  </div>
                  
                  {/* Force check chart type vs data availability */}
                  {chartType === 'premium' && (!premiumData || premiumData.length === 0) && (
                    <div className="p-4 bg-yellow-100 text-yellow-800 mb-4 rounded">
                      Chart type is set to 'premium' but no premium data is available.
                      <button 
                        onClick={() => setChartType('index')} 
                        className="ml-2 text-blue-600 underline"
                      >
                        Switch to Index Chart
                      </button>
                    </div>
                  )}
                  
                  {/* Strategy-specific chart rendering */}
                  {(() => {
                    // Determine which chart to use based on strategy type
                    const strategyType = result.strategy_type || result.strategy_name || 'default';
                    console.log('Using chart for strategy:', strategyType);
                    
                    if (strategyType.toLowerCase().includes('fractal')) {
                      return (
                        <FractalChart 
                          data={chartType === 'index' ? priceData : premiumData} 
                          trades={result.trades || []}
                          showSupertrend={chartType === 'premium'}
                          key={`fractal-${chartType}-chart-${premiumData?.length || 0}-${priceData?.length || 0}`}
                        />
                      );
                    } else {
                      // Fallback to StableChart for other strategies
                      return (
                        <StableChart 
                          data={chartType === 'index' ? priceData : premiumData} 
                          trades={result.trades || []}
                          chartType={chartType}
                          showSupertrend={chartType === 'premium'}
                          key={`stable-${chartType}-chart-${premiumData?.length || 0}-${priceData?.length || 0}`}
                        />
                      );
                    }
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Equity Curve Tab */}
        {activeTab === 'equity' && (
          <div className="bg-white shadow overflow-hidden rounded-lg" style={{ width: '100%' }}>
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Equity Curve
              </h3>
            </div>
            <div className="p-6" style={{ width: '100%', maxWidth: '100%' }}>
              {result.equity_curve && result.equity_curve.length > 0 ? (
                <div className="equity-chart-container" style={{ height: '500px', width: '100%', maxWidth: '100vw', position: 'relative' }}>
                  <Line 
                    data={prepareEquityCurveData()}
                    options={{
                      ...chartOptions,
                      responsive: true,
                      maintainAspectRatio: false,
                      aspectRatio: 2.5
                    }}
                    style={{ width: '100%' }}
                  />
                </div>
              ) : (
                <div className="text-center text-gray-500">
                  No equity curve data available.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Parameters Tab */}
        {activeTab === 'parameters' && (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Strategy Parameters
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Strategy</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {result.strategy_name}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Symbol</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {result.symbol.toUpperCase()}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Date Range</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {result.start_date} to {result.end_date}
                  </dd>
                </div>
                {Object.entries(result.parameters).map(([key, value]) => (
                  <div key={key} className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim().charAt(0).toUpperCase() + key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim().slice(1)}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {typeof value === 'number' ? 
                        (value % 1 === 0 ? value : value.toFixed(2)) : 
                        String(value)
                      }
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BacktestResults;

